import { Component, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, FormsModule, Validators } from '@angular/forms';
import { ITagType, TAG_TYPES } from 'src/app/contract-management/models/settings.model';
import { ProjectTagService } from 'src/app/contract-management/services/project-tag.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { ButtonComponent } from "src/app/@core/components/ui/button/button.component";
import { CheckboxComponent } from "src/app/@core/components/ui/checkbox/checkbox.component";
import { Skeleton } from "primeng/skeleton";
import { Card } from "primeng/card";
import { getRandomColor } from 'src/app/common/utils';
import { SearchBarComponent } from 'src/app/@shared/components/search-bar/search-bar.component';
import { FileSelectionComponent } from "src/app/@shared/components/file-selection/file-selection.component";
import { FetchFunction, FileItem, FileSelectionConfig, FileSelectionSubmitEvent } from "src/app/@shared/components/file-selection/file-selection.model";
import { TagsService } from 'src/app/contract-management/services/tags.service';
import { map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TAG_DATA_TYPES } from 'src/app/@core/models/enums';
import { SVG } from 'src/app/common/svgs';
import { SanitizeHtmlPipe } from "../../../../../@shared/pipes/sanitizeHtml.pipe";
import { IconComponent } from "src/app/@core/components/ui/icon/icon.component";

@Component({
    selector: 'app-project-status',
    imports: [
        FormsModule,
        ButtonComponent,
        CheckboxComponent,
        Skeleton,
        Card,
        SearchBarComponent,
        FileSelectionComponent,
        IconComponent
    ],
    standalone: true,
    templateUrl: './project-tags.component.html',
    styleUrls: ['./project-tags.component.scss']
})
export class ProjectTagsComponent implements OnDestroy {

    private searchSubject = new Subject<string>();

    constructor(private projectTagService: ProjectTagService, private toast: ToastService, private tagService: TagsService) { }

    ngOnInit() {
        this.filterFixedProjectTags(this.searchProjectTagType);
        this.getProjectTagType(this.searchProjectTagType);
        this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(searchValue => {
            this.getProjectTagType(searchValue);
        });
    }

    ngOnDestroy() {
        this.searchSubject.complete();
    }

    fixedProjectTags = [
        { name: 'Project Name', icon: 'pi pi-folder-open' },
        { name: 'Project ID', icon: 'pi pi-hashtag' },
        { name: 'Project Admins', icon: 'pi pi-users' },
        { name: 'Customer/Prospect Name', icon: 'pi pi-briefcase' },
        { name: 'Due Date', icon: 'pi pi-calendar' }
    ];

    filteredFixedProjectTags = [...this.fixedProjectTags];
    connectProjectTagDialogVisible: boolean = false;
    projectTagTypeLoading: boolean = false;
    projectTagType: ITagType[] = [];
    draggedTagIdx: number | null = null;
    searchProjectTagType: string = '';
    projectTagTypeMap: { [key: string]: boolean } = {}

    fileSelectionConfig: FileSelectionConfig = {
        title: 'Connect Tag Types',
        submitButtonLabel: 'Connect Selected',
        showSearch: true,
        searchPlaceholder: 'Search tag types',
        selectedPanelTitle: 'Selected Tag Types',
        selectedEmptyMessage: 'Select tag types to add them'
    };

    fetchTagTypes: FetchFunction = (event) => {
        return this.tagService.getTagTypes({
            filters: { page: event.page + 1, limit: event.pageSize, searchValue: event.searchQuery },
            getLinkedTags: false,
        }).pipe(
            map(res => {
                const raw = res?.data?.tagTypes || [];
                const items: FileItem[] = raw
                    .filter((tag: ITagType) => tag.tagType !== TAG_TYPES.REPO_EXTRACTED)
                    .map((tag: ITagType) => ({
                        id: tag._id ?? '',
                        name: tag.name,
                        metadata: { originalTag: tag },
                    }));
                return { items, totalRecords: res.data?.total ?? items.length };
            })
        );
    };

    // icons for tag types and data types
    TAG_TYPES_ICONS: { [key: string]: string } = {
        [TAG_TYPES.CLIENT]: 'pi pi-user',
        [TAG_TYPES.LOCATION]: 'pi pi-map-marker',
        [TAG_TYPES.SCOPE_OF_WORK]: 'pi pi-bullseye',
        [TAG_TYPES.TYPE_OF_CONTRACT]: 'pi pi-file',
    };

    TAG_DATA_TYPES_ICONS: { [key: string]: string } = {
        [TAG_DATA_TYPES.TEXT]: SVG.TEXT_TAG_DATA_TYPE,
        [TAG_DATA_TYPES.NUMBER]: SVG.NUMBER_TAG_DATA_TYPE,
        [TAG_DATA_TYPES.DATE]: SVG.DATE_TAG_DATA_TYPE,
        [TAG_DATA_TYPES.DROPDOWN]: SVG.DROPDOWN_FIELDS_TAG_DATA_TYPE,
    };

    getSkeletonCount(count: number) {
        return Array(count).fill(0);
    }

    getRandomColor(): string {
        return getRandomColor()
    }

    dragStart(index: number) {
        this.draggedTagIdx = index;
    }

    dropTag(dropIdx: number) {
        if (this.draggedTagIdx !== null && this.draggedTagIdx !== dropIdx) {
            const draggedTag = this.projectTagType[this.draggedTagIdx];
            this.projectTagType.splice(this.draggedTagIdx, 1);
            this.projectTagType.splice(dropIdx, 0, draggedTag);
            this.draggedTagIdx = null;
        }
    }

    searchProjectTagTypeChange(event: string) {
        this.searchProjectTagType = event;
        this.filterFixedProjectTags(event);
        this.searchSubject.next(this.searchProjectTagType);
    }

    filterFixedProjectTags(searchValue: string) {
        if (!searchValue || searchValue.trim() === '') {
            this.filteredFixedProjectTags = [...this.fixedProjectTags];
        } else {
            const searchLower = searchValue.toLowerCase().trim();
            this.filteredFixedProjectTags = this.fixedProjectTags.filter(tag =>
                tag.name.toLowerCase().includes(searchLower)
            );
        }
    }

    getProjectTagType(searchValue: string) {
        this.projectTagTypeLoading = true;
        this.projectTagService.getProjectTagType(searchValue).subscribe((res: any) => {
            if (res.success) {
                this.projectTagType = res.data;
                this.projectTagTypeLoading = false;
                this.projectTagTypeMap = this.projectTagType.reduce((acc, cur) => { acc[cur._id ?? ''] = true; return acc }, {} as { [key: string]: boolean })
            }
        });
    }

    delinkLinkedTag(tag: ITagType) {
        this.projectTagService.delinkProjectTagType(tag._id ?? '').subscribe((res: any) => {
            if (res.success) {
                this.toast.success('Tag Type delinked successfully');
                this.projectTagType = this.projectTagType.filter(item => item._id !== tag._id);
                this.getProjectTagType(this.searchProjectTagType);
            }
        });
    }

    openConnectProjectTagDialog() {
        this.fileSelectionConfig = {
            ...this.fileSelectionConfig,
            initialSelectedFiles: this.projectTagType.map(tag => ({
                id: tag._id ?? '',
                name: tag.name,
                metadata: { originalTag: tag },
            })),
        };
        this.connectProjectTagDialogVisible = !this.connectProjectTagDialogVisible;
    }

    submitProjectTagSelection(event: FileSelectionSubmitEvent) {
        const selectedTagTypes = (event.allSelectedFiles ?? []).map(f => f.metadata?.originalTag as ITagType).filter(Boolean);

        const tagTypeIdsAdded = selectedTagTypes.filter(t => !this.projectTagTypeMap[t._id ?? '']).map(t => t._id);
        const tagTypeIdsRemoved = this.projectTagType.filter(t => !selectedTagTypes.some(st => st._id === t._id)).map(t => t._id);

        this.projectTagService.addUpdateProjectTagType(tagTypeIdsAdded as string[], tagTypeIdsRemoved as string[]).subscribe(res => {
            if (res?.success) {
                this.toast.success('Connected Tag Types successfully');
                this.connectProjectTagDialogVisible = false;
                this.getProjectTagType(this.searchProjectTagType);
            }
        });
    }

}